import { createSelector } from '@reduxjs/toolkit';
import { AppFlow } from 'Container/App/interface';
import {
    selectAvailableSubscriptionIds,
    selectCustomerConfig,
} from 'Container/App/slice';
import {
    isTariffWithHardwareResponseData,
    SimOnlyOffer,
} from '@vfde-sails/glados-v2';
import {
    API_BTX_BNT,
    API_BTX_VVL,
    API_SALESCHANNEL_CONSUMER,
    MAP_SALESCHANNEL_TO_API_SALESCHANNEL,
    RedplusTariff,
    RedTariff,
    YoungTariff,
} from '@vfde-sails/constants';
import { gladosApi } from 'Container/App/api/glados';
import { RootState } from '../../store';
import { createStructuredSelector } from 'reselect';

const createAppSelector = createSelector.withTypes<RootState>();

const getSelectorForRedPlusQuery = createAppSelector(
    selectAvailableSubscriptionIds,
    tariffIds => gladosApi.endpoints.getSimOnly.select({
        tariffIds,
        salesChannel: [API_SALESCHANNEL_CONSUMER],
        btx: API_BTX_BNT,
    }),
);

const getSelectorForVvlQuery = createAppSelector(
    selectCustomerConfig,
    customerConfig => {
        if (customerConfig.atomicId) {
            return gladosApi.endpoints.getTariffWithHardware.select({
                tariffIds: [customerConfig.activeSubscriptionId!],
                salesChannel: [MAP_SALESCHANNEL_TO_API_SALESCHANNEL[customerConfig.salesChannel]],
                btx: API_BTX_VVL,
                atomicIds: [customerConfig.atomicId],
            });
        }

        return gladosApi.endpoints.getSimOnly.select({
            tariffIds: [customerConfig.activeSubscriptionId!],
            salesChannel: [MAP_SALESCHANNEL_TO_API_SALESCHANNEL[customerConfig.salesChannel]],
            btx: API_BTX_VVL,
        });
    },
);

const selectRedPlusQuery = createAppSelector(
    [
        state => state,
        getSelectorForRedPlusQuery,
    ],
    (state, selector) => selector(state),
);

const selectVvlOfferQuery = createAppSelector(
    [
        state => state,
        getSelectorForVvlQuery,
    ],
    (state, selector) => selector(state),
);

const selectRedPlusOffers = createAppSelector(
    [
        selectRedPlusQuery,
    ],
    // TODO: is there a better way instead of typecasting?
    redPlusQuery => redPlusQuery.data?.data.tariffs as SimOnlyOffer<RedplusTariff>[] || [],
);

const selectLoading = createAppSelector(
    [
        selectRedPlusQuery,
        selectVvlOfferQuery,
    ],
    (redPlusQuery, vvlOfferQuery) => redPlusQuery.isLoading || vvlOfferQuery.isLoading,
);

const selectHasError = createAppSelector(
    [
        selectRedPlusQuery,
        selectVvlOfferQuery,
    ],
    (redPlusQuery, vvlOfferQuery) => redPlusQuery.isError || vvlOfferQuery.isError,
);

const selectVvlTariffOffer = createAppSelector(
    [
        selectCustomerConfig,
        selectVvlOfferQuery,
    ],
    (customerConfig, vvlOfferQuery) => {
        const data = vvlOfferQuery.data?.data;

        if (!data) {
            return null;
        }

        if (isTariffWithHardwareResponseData(data)) {
            return data
                .find(offer => offer.hardware.hardwareId === customerConfig.atomicId)?.tariffs
                .find(subscription => subscription.virtualItemId === customerConfig.activeSubscriptionId) || null;
        }

        // TODO: is there a better way instead of typecasting?
        return data.tariffs
            .find(
                subscription => subscription.virtualItemId === customerConfig.activeSubscriptionId,
            ) as SimOnlyOffer<RedTariff | YoungTariff> || null;
    },
);

const selectActiveRedPlusOffer = createAppSelector(
    [
        selectCustomerConfig,
        selectRedPlusOffers,
    ],
    ({ selectedRedplusId }, redPlusOffers) => {
        if (!selectedRedplusId || redPlusOffers.length === 0) {
            return null;
        }

        return redPlusOffers.find(offer => offer.virtualItemId === selectedRedplusId) || null;
    },
);

const selectMainOffer = createAppSelector(
    [
        selectCustomerConfig,
        selectActiveRedPlusOffer,
        selectVvlTariffOffer,
    ],
    ({ flow }, redPlusOffer, vvlOffer) => {
        if (!redPlusOffer && !vvlOffer) {
            return null;
        }

        if (flow === AppFlow.Vvl) {
            return vvlOffer;
        }

        return redPlusOffer;
    },
);

const selectSecondaryOffer = createAppSelector(
    [
        selectCustomerConfig,
        selectActiveRedPlusOffer,
    ],
    ({ flow }, redPlusOffer) => {
        if (!redPlusOffer) {
            return null;
        }

        return (flow === AppFlow.Vvl && redPlusOffer) || null;
    },
);

const createStructuredAppSelector =
    createStructuredSelector.withTypes<RootState>();

export const mapStateToProps = createStructuredAppSelector({
    isLoading: selectLoading,
    hasError: selectHasError,
    customerConfig: selectCustomerConfig,
    redPlusOffers: selectRedPlusOffers,
    vvlTariffOffer: selectVvlTariffOffer,
});

export {
    selectLoading,
    selectHasError,
    selectAvailableSubscriptionIds,
    selectCustomerConfig,
    selectVvlTariffOffer,
    selectRedPlusOffers,
    selectActiveRedPlusOffer,
    selectMainOffer,
    selectSecondaryOffer,
};
